// Lender-rate freshness hook + formatter.
//
// Drives the "● Updated weekly" chips on Hero and TrustBar. Pulls the
// most recent lender_rates.updated_at from /api/stats and formats it as
// "Updated Feb 16". Falls back to the static "Updated weekly" string on
// failure or if the timestamp is stale (>14 days) — that way the public
// promise stays defensible if the cron drifts.
import { useEffect, useState } from "react";
import { fetchStats } from "./api";

const STALE_AFTER_DAYS = 14;
const SESSION_KEY = "carsavr:freshness:v1";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min — keeps the public chip cheap

let inflight = null;

async function loadFreshness() {
  // Session-cache so we don't re-hit /api/stats on every route change.
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached && Date.now() - cached.fetched_at < SESSION_TTL_MS) {
        return cached.value;
      }
    }
  } catch {
    // sessionStorage unavailable — fall through to network
  }

  if (!inflight) {
    inflight = fetchStats()
      .then((data) => data?.lender_rates_last_refresh_at || null)
      .catch(() => null)
      .finally(() => {
        // Re-arm dedupe after the promise settles so future cache misses retry
        setTimeout(() => {
          inflight = null;
        }, 0);
      });
  }
  const value = await inflight;
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ value, fetched_at: Date.now() }),
    );
  } catch {
    /* noop */
  }
  return value;
}

/**
 * Format an ISO timestamp as "Updated MMM D" (e.g. "Updated Feb 16").
 * Returns the static fallback string when the date is missing or stale.
 */
export function formatFreshness(iso, fallback = "Updated weekly") {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  const ageDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays > STALE_AFTER_DAYS || ageDays < 0) return fallback;
  const month = d.toLocaleString("en-US", { month: "short" });
  return `Updated ${month} ${d.getDate()}`;
}

/**
 * React hook — returns a display-ready freshness label.
 * Always starts with the fallback so SSR/first-paint never blocks.
 */
export function useRatesFreshness(fallback = "Updated weekly") {
  const [label, setLabel] = useState(fallback);
  useEffect(() => {
    let cancelled = false;
    loadFreshness().then((iso) => {
      if (cancelled) return;
      setLabel(formatFreshness(iso, fallback));
    });
    return () => {
      cancelled = true;
    };
  }, [fallback]);
  return label;
}
